// PersonalityQuiz.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import styles from "./PersonalityQuiz.module.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { PBPIpt } from "../../data/PBPI";
import { PBPIen } from "../../data/PBPI";

import { updateUserFirebase } from "../../services/firebaseService";
import { UserContext } from "../../context/UserContext";
import { getArchetypeFromTraits } from "../../utils/archetypes27";
import { submitAnonymous } from "../../services/quizService";
import defaultAvatar from "../../assets/personality/breeze.png";
import ReportPreviewFree from "../../components/Report/ReportPreviewFree";
import images from "../../assets/personality";

const PBPI = {
  pt: PBPIpt,
  en: PBPIen,
};
const STORAGE_KEY = "ibahva.quiz.draft";
const PREVIEW_TOKEN_KEY = "ibahva.quiz.previewToken";

export default function PersonalityQuiz() {
  const { userData, setUserData, user } = useContext(UserContext);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const { i18n, t } = useTranslation("personalityQuiz");

  const questions = useMemo(() => {
    const lang = i18n.language.startsWith("pt") ? "pt" : "en";
    return PBPI[lang];
  }, [i18n.language]);

  const stepLength = questions.length + 2;

  // initialize checks: try to load draft from localStorage
  const initialDraft = useMemo(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed.PBPICheck || null;
      }
    } catch (e) {}
    // default
    return Array(45)
      .fill(false)
      .map(() => Array(5).fill(false));
  }, []);

  const [PBPICheck, setPBPICheck] = useState(
    initialDraft ||
      Array(45)
        .fill(false)
        .map(() => Array(5).fill(false))
  );
  const [submitting, setSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPayload, setPreviewPayload] = useState(null);
  const [previewToken, setPreviewToken] = useState(
    localStorage.getItem(PREVIEW_TOKEN_KEY) || null
  );

  // persist draft on changes
  useEffect(() => {
    const toSave = { PBPICheck, updatedAt: new Date().toISOString() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (e) {
      console.warn("Could not save quiz draft", e);
    }
  }, [PBPICheck]);

  function handleCheckPBPI(row, col) {
    setPBPICheck((prev) => {
      const updated = prev.map((r) => r.slice());
      updated[row] = updated[row].map((_, i) => i === col);
      return updated;
    });
  }

  function reverseItem(item) {
    return 6 - item;
  }

  function computePBPIReduced() {
    return PBPICheck.map((row) => {
      const idx = row.findIndex((r) => r);
      return idx >= 0 ? idx + 1 : 0;
    });
  }

  function currentQuestionAnswered(i) {
    return PBPICheck[i]?.includes(true);
  }

  // submit() — only computes locally and updates userData; submitAnonFlow() handles server preview
  function submitLocal() {
    const PBPIReduced = computePBPIReduced();
    const firstUnansweredIndex = PBPIReduced.findIndex((v) => v === 0);
    if (firstUnansweredIndex >= 0) {
      setStep(firstUnansweredIndex + 2);
      window.alert(
        "Por favor, responda todas as questões antes de finalizar o questionário."
      );
      return null;
    }

    const emotionality =
      PBPIReduced[0] +
      PBPIReduced[7] +
      reverseItem(PBPIReduced[14]) +
      PBPIReduced[21] +
      reverseItem(PBPIReduced[28]) +
      PBPIReduced[34] +
      reverseItem(PBPIReduced[40]);

    const activity =
      PBPIReduced[1] +
      reverseItem(PBPIReduced[8]) +
      PBPIReduced[15] +
      reverseItem(PBPIReduced[22]) +
      PBPIReduced[29] +
      reverseItem(PBPIReduced[35]) +
      PBPIReduced[41];

    const sociability =
      PBPIReduced[2] +
      reverseItem(PBPIReduced[9]) +
      PBPIReduced[16] +
      reverseItem(PBPIReduced[23]) +
      PBPIReduced[30] +
      reverseItem(PBPIReduced[36]) +
      PBPIReduced[42];

    const ruleFollowing =
      PBPIReduced[3] +
      reverseItem(PBPIReduced[10]) +
      PBPIReduced[17] +
      reverseItem(PBPIReduced[24]) +
      PBPIReduced[31] +
      reverseItem(PBPIReduced[37]) +
      PBPIReduced[43];

    const cognitiveFlexibility =
      PBPIReduced[4] +
      PBPIReduced[11] +
      reverseItem(PBPIReduced[18]) +
      PBPIReduced[25] +
      reverseItem(PBPIReduced[32]) +
      PBPIReduced[38];

    const psychologicalFlexibility =
      PBPIReduced[5] +
      reverseItem(PBPIReduced[12]) +
      PBPIReduced[19] +
      reverseItem(PBPIReduced[26]) +
      PBPIReduced[33] +
      reverseItem(PBPIReduced[39]) +
      reverseItem(PBPIReduced[44]);

    const perspectiveTaking =
      PBPIReduced[6] +
      PBPIReduced[13] +
      reverseItem(PBPIReduced[20]) +
      reverseItem(PBPIReduced[27]);

    const personalityTraits = [
      Math.round((emotionality * 100) / 35),
      Math.round((activity * 100) / 35),
      Math.round((sociability * 100) / 35),
      Math.round((ruleFollowing * 100) / 35),
      Math.round((cognitiveFlexibility * 100) / 30),
      Math.round((psychologicalFlexibility * 100) / 35),
      Math.round((perspectiveTaking * 100) / 20),
    ];

    // derive archetype
    const temperament = personalityTraits.slice(0, 3);
    const archetype = getArchetypeFromTraits(temperament) || {};

    // audit entry
    const auditEntry = {
      action: "quiz_submitted",
      at: new Date().toISOString(),
      by: userData?.id || null,
      archetypeKey: archetype?.key || archetype?.label || null,
    };

    // update local userData context
    const updatedUserData = {
      ...userData,
      personalityTraits,
      personalityAudit: [...(userData?.personalityAudit || []), auditEntry],
    };
    setUserData && setUserData(updatedUserData);

    // persist to backend only if logged
    const userId = userData?.id || user?.uid || null;
    if (userId) {
      (async () => {
        try {
          await updateUserFirebase(updatedUserData);
        } catch (err) {
          console.error("Failed to update user:", err);
        }
      })();
    } else {
      // save locally for later conversion
      try {
        localStorage.setItem(
          "ibhava_last_quiz",
          JSON.stringify({
            at: new Date().toISOString(),
            payload: updatedUserData,
          })
        );
      } catch (e) {
        console.warn("Could not save quiz locally", e);
      }
    }

    // compute insights (use your computeInsights if available)
    const insights = {
      temperamentStrengths: ["Clareza mental", "Visão ampla"],
      characterResources: ["Ações pequenas", "Desfusão"],
      recommendations: ["Regra dos 5 minutos", "Diário breve"],
      miniPlan: [
        { title: "Dia 1", text: "Observação sem julgamento (10 min)" },
        { title: "Dia 2", text: "Desfusão (5 min)" },
      ],
      weeklyQuestion: "O que você valoriza mais?",
    };

    // build a FULL payload explicitly from computed values (use defaults)
    const payload = {
      name:
        (userData && userData.displayName) || (user && user.name) || "Usuário",
      date: new Date().toLocaleDateString(),
      personalityType:
        (archetype?.clan && archetype.clan.label) ||
        archetype?.label ||
        archetype?.key ||
        "—",
      archetype_label: archetype?.label || archetype?.key || "—",
      archetype_image:
        images[archetype?.clan?.key] ||
        defaultAvatar ||
        "/assets/personality/breeze.png",
      summary_executive: "Resumo curto gerado automaticamente.",
      temperamentStrengths: insights.temperamentStrengths,
      characterResources: insights.characterResources,
      miniPlan: insights.miniPlan,
      recommendations: insights.recommendations.map((r) => ({
        title: r,
        text: "",
      })),
      weeklyQuestion: insights.weeklyQuestion,
      personalityTraits,
    };

    return { payload, archetype, insights, updatedUserData };
  }

  // submit anonymously to get previewToken and preview-ready payload
  // PersonalityQuiz.jsx -> submitAnonymousFlow (replace)
  async function submitAnonymousFlow() {
    const localResult = submitLocal();
    if (!localResult) return;
    setSubmitting(true);
    try {
      // call backend (if exists) - it may return previewToken
      let apiResp = null;
      try {
        apiResp = await submitAnonymous({
          answers: computePBPIReduced(),
          meta: { source: "web" },
          payload: localResult.payload,
        });
      } catch (e) {
        console.warn(
          "submitAnonymousFlow: submitAnonymous failed or not implemented",
          e
        );
        apiResp = null;
      }

      // Always persist the local payload to localStorage (fallback)
      try {
        localStorage.setItem(
          "ibhava_last_quiz_payload",
          JSON.stringify(localResult.payload)
        );
      } catch (e) {
        console.warn("Could not save ibhava_last_quiz_payload", e);
      }

      // If API returned previewToken, persist with canonical name
      const token = apiResp?.previewToken || null;
      if (token) {
        try {
          localStorage.setItem("ibhava_preview_token", token);
          // also store legacy key for compatibility
          localStorage.setItem("ibahva.quiz.previewToken", token);
        } catch (e) {
          console.warn("Could not save ibhava_preview_token", e);
        }
        setPreviewToken(token);
      }

      // Build previewPayload: ALWAYS start from local payload (so we don't lose fields)
      const previewPayload = {
        ...localResult.payload,
        ...(apiResp?.result?.insights ? apiResp.result.insights : {}),
      };

      setPreviewPayload(previewPayload);
      setPreviewOpen(true);
    } catch (err) {
      console.error("submitAnonymousFlow error", err);
      setPreviewPayload(localResult.payload);
      setPreviewOpen(true);
    } finally {
      setSubmitting(false);
    }
  }

  // UI: show result modal after finished step
  const [showResultModal, setShowResultModal] = useState(false);
  // useEffect(() => {
  //   if (step === PBPI.length + 2) {
  //     // open result modal automatically (small delay)
  //     setTimeout(() => setShowResultModal(true), 220);
  //   }
  // }, [step]);

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <header className={styles.topBar}>
          <div className={styles.progressBarBackground}>
            <div
              className={styles.progressBarFill}
              style={{ width: `${(step / stepLength) * 100}%` }}
            />
          </div>
        </header>

        <main className={styles.content}>
          {step === 0 && (
            <>
              <h3 className={styles.h3}>{t("title1")}</h3>
              <p className={styles.text}>{t("text1")}</p>
            </>
          )}

          {step === 1 && (
            <>
              <h3 className={styles.h3}>{t("title2")}</h3>
              <p className={styles.text}>{t("text2")}</p>
            </>
          )}

          {/* Questions */}
          {questions.map((question, i) =>
            step === i + 2 ? (
              <div key={i} className={styles.questionBox}>
                <h4 className={styles.h3}>{question}</h4>
                <div className={styles.options}>
                  {t("options", { returnObjects: true }).map(
                    (label, option) => (
                      <button
                        key={option}
                        className={`${styles.option} ${
                          PBPICheck[i][option] ? styles.optionSelected : ""
                        }`}
                        onClick={() => handleCheckPBPI(i, option)}
                        type="button"
                      >
                        {label}
                      </button>
                    )
                  )}
                </div>
              </div>
            ) : null
          )}

          {step === questions.length + 2 && (
            <>
              <h3 className={styles.h3}>{t("title3")}</h3>
              <section className={styles.resultBox}>
                <p>{t("text3")}</p>

                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  {!userData ? (
                    <button
                      className={styles.primaryBtn}
                      onClick={async () => {
                        const localResult = submitLocal();
                        if (!localResult) return;

                        if (!user) {
                          // store payload / preview token locally so we can attach after login
                          try {
                            localStorage.setItem(
                              "ibhava_last_quiz_payload",
                              JSON.stringify(localResult.payload)
                            );
                          } catch (e) {}
                          // redirect to login; after login flow should check localStorage and call convertAnonymous
                          window.location.href = "/login?next=/profile";
                          return;
                        }

                        // logged: attach immediately
                        try {
                          await updateUserFirebase(
                            localResult.updatedUserData ||
                              localResult.updatedUserData
                          );
                          // ensure userData in context is synced
                          setUserData &&
                            setUserData(
                              localResult.updatedUserData ||
                                localResult.updatedUserData
                            );
                          // go to profile
                          navigate("/profile");
                        } catch (err) {
                          console.error("Erro ao salvar perfil:", err);
                          alert(t("alert"));
                        }
                      }}
                    >
                      {t("loginSaveButton")}
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        const localResult = submitLocal();
                        if (!localResult) return;
                        // logged: attach immediately
                        try {
                          await updateUserFirebase(
                            localResult.updatedUserData ||
                              localResult.updatedUserData
                          );
                          // ensure userData in context is synced
                          setUserData &&
                            setUserData(
                              localResult.updatedUserData ||
                                localResult.updatedUserData
                            );
                          // go to profile
                          navigate("/profile");
                        } catch (err) {
                          console.error("Erro ao salvar perfil:", err);
                          alert(t("alert"));
                        }
                      }}
                      className={styles.primaryBtn}
                    >
                      {t("saveButton")}
                    </button>
                  )}
                  {!userData && (
                    <button
                      className={styles.secondaryBtn}
                      onClick={() => {
                        const localResult = submitLocal();
                        if (!localResult) return;

                        if (!user) {
                          // store payload / preview token locally so we can attach after login
                          try {
                            localStorage.setItem(
                              "ibhava_last_quiz_payload",
                              JSON.stringify(localResult.payload)
                            );
                          } catch (e) {}
                          window.location.href = "/results";
                          return;
                        }
                      }}
                      disabled={submitting}
                    >
                      {submitting ? t("preparing") : t("viewResults")}
                    </button>
                  )}

                  {/* <button
                    className={styles.secondaryBtn}
                    onClick={() => {
                      // buy flow: require login
                      if (!user) {
                        window.location.href = "/login?next=/purchase";
                        return;
                      }
                      // if logged, go to purchase flow (implement)
                      window.location.href = "/purchase";
                    }}
                  >
                    Comprar relatório premium
                  </button> */}
                </div>
              </section>
            </>
          )}
        </main>

        <footer className={styles.buttonContainer}>
          <button
            className={styles.primaryBtn}
            onClick={() => step > 0 && setStep(step - 1)}
            disabled={step <= 0}
          >
            {t("prevButton")}
          </button>

          {step !== PBPI.length + 2 && (
            <button
              className={styles.primaryBtn}
              onClick={() => {
                if (step < stepLength) setStep(step + 1);
              }}
              disabled={step >= 2 && !currentQuestionAnswered(step - 2)}
            >
              {t("nextButton")}
            </button>
          )}
        </footer>
      </div>

      {/* Result modal (simple) */}
      {showResultModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowResultModal(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalContent}>
              <h3>{t("savedResultTitle")}</h3>
              <p>{t("savedResultText")}</p>
              <div className={styles.modalActions}>
                <button
                  className={styles.primaryBtn}
                  onClick={() => {
                    // if user logged, go to profile; if not, redirect to login (and keep payload saved)
                    if (user) {
                      navigate("/profile");
                    } else {
                      window.location.href = "/login?next=/profile";
                    }
                  }}
                >
                  {t("viewProfileButton")}
                </button>

                <button
                  className={styles.secondaryBtn}
                  onClick={() => setShowResultModal(false)}
                >
                  {t("closeButton")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report preview modal */}
      <ReportPreviewFree
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        payload={previewPayload}
        // when preview token exists, you could pass it to ReportPreview if needed
      />
    </div>
  );
}
